<template>
    <div class="max-w-(--breakpoint-sm) flex flex-col gap-8">
        <page-header class="mb-0!">Intelligent Inbox</page-header>

        <vx-expandable-card color="info">
            <template v-slot:content>
                <div class="flex flex-col gap-6">
                    <div>
                        <span class="font-extrabold text-2xl text-slate-700">Categories</span>
                        <p class="text-sm text-slate-500 mb-0">
                            Configure how Intelligent Inbox classifies and prioritizes conversations.
                        </p>
                    </div>

                    <page-loading v-if="loading"></page-loading>

                    <vx-alert v-if="!loading && loadError" color="danger" :closeable="false" shadow>
                        <div class="flex items-center justify-between">
                            Failed to load categories
                            <vx-button data-test="retry-btn" @click="fetchCategories" size="sm" color="danger">
                                Retry <font-awesome-icon widthAuto :icon="faRefresh" />
                            </vx-button>
                        </div>
                    </vx-alert>

                    <template v-if="!loading && !loadError">
                        <vx-alert v-if="dirty" color="info" :closeable="false">
                            <div class="flex items-center justify-between gap-4">
                                <span>You have unsaved changes. Save to apply them.</span>
                                <vx-button
                                    data-test="save-shortcut-btn"
                                    size="sm"
                                    color="info"
                                    :loading="submitting"
                                    @click.prevent="save"
                                >
                                    Save
                                </vx-button>
                            </div>
                        </vx-alert>

                        <vx-alert v-if="categories.length === 0" color="primary" :closeable="false">
                            Add your first category to enable Intelligent Inbox for your groups.
                        </vx-alert>

                        <div v-if="categories.length" class="flex flex-col gap-2">
                            <div ref="sortableEl" class="flex flex-col gap-2">
                                <div
                                    v-for="(category, index) in categories"
                                    :key="category.id ?? category.name + '-' + index"
                                    class="category-draggable flex items-center"
                                >
                                    <span
                                        class="category-handle pr-4 py-4"
                                        :class="submitting ? 'pointer-events-none opacity-50' : 'cursor-move'"
                                    >
                                        <font-awesome-icon widthAuto :icon="faGripDotsVertical"></font-awesome-icon>
                                    </span>
                                    <intelligent-inbox-category-card
                                        class="grow"
                                        :modelValue="categories[index]!"
                                        :disabled="submitting"
                                        @update:modelValue="(value: ThreadCategoryType) => updateCategory(index, value)"
                                        @edit="openEdit(index)"
                                        @delete="removeCategory(index)"
                                    />
                                </div>
                            </div>
                        </div>

                        <div v-if="categories.length < MAX_CATEGORIES" class="flex">
                            <vx-box class="p-4">
                                <vx-button
                                    data-test="new-category-btn"
                                    size="lg"
                                    :disabled="submitting"
                                    @click="openCreate"
                                >
                                    New Category
                                    <font-awesome-icon :icon="faCirclePlus"></font-awesome-icon>
                                </vx-button>
                            </vx-box>
                        </div>

                        <vx-alert v-if="categories.length >= MAX_CATEGORIES" color="info" :closeable="false">
                            Maximum of {{ MAX_CATEGORIES }} categories reached.
                        </vx-alert>

                        <vx-button
                            block
                            size="lg"
                            data-test="save-btn"
                            :loading="submitting"
                            :disabled="!dirty"
                            @click.prevent="save"
                        >
                            Save
                        </vx-button>
                    </template>
                </div>
            </template>
        </vx-expandable-card>

        <membership-status-definitions-settings />
        <lifecycle-status-definitions-settings />

        <modal-thread-category
            v-model:visible="modalVisible"
            :category="modalCategory"
            :editIndex="modalEditIndex"
            @saved="onCategorySaved"
        />
    </div>
</template>

<script lang="ts" setup>
import { faCirclePlus, faGripDotsVertical, faRefresh } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxBox, VxButton, VxExpandableCard, VxSwal } from '@voxie/frontend-components';
import { moveArrayElement, useSortable } from '@vueuse/integrations/useSortable';
import { computed, nextTick, ref, watch } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import IntelligentInboxCategoryCard from './IntelligentInboxCategoryCard.vue';
import LifecycleStatusDefinitionsSettings from './LifecycleStatusDefinitionsSettings.vue';
import MembershipStatusDefinitionsSettings from './MembershipStatusDefinitionsSettings.vue';
import ModalThreadCategory from './ModalThreadCategory.vue';
import { threadCategoriesClient } from '~/services';
import type {
    ThreadCategoryFormData,
    ThreadCategoryType,
} from '~/services/threadCategories/threadCategoriesClient.types';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';

const MAX_CATEGORIES = 20;
const teamId = Spark.state.currentTeam.id;
const toasted = useToasted();

const loading = ref(false);
const loadError = ref(false);
const submitting = ref(false);
const categories = ref<ThreadCategoryType[]>([]);
const originalCategories = ref<ThreadCategoryType[]>([]);

const modalVisible = ref(false);
const modalCategory = ref<ThreadCategoryType | null>(null);
const modalEditIndex = ref<number | null>(null);

const toFormData = (category: ThreadCategoryType): ThreadCategoryFormData => ({
    name: category.name,
    description: category.description,
    classification_instructions: category.classification_instructions,
    color: category.color,
});

const serialize = (list: ThreadCategoryType[]): string => JSON.stringify(list.map(toFormData));

const dirty = computed(() => serialize(categories.value) !== serialize(originalCategories.value));

const openCreate = (): void => {
    modalCategory.value = null;
    modalEditIndex.value = null;
    modalVisible.value = true;
};

const openEdit = (index: number): void => {
    modalCategory.value = categories.value[index] ?? null;
    modalEditIndex.value = index;
    modalVisible.value = true;
};

const updateCategory = (index: number, updatedCategory: ThreadCategoryType): void => {
    if (undefined === categories.value[index]) {
        return;
    }
    categories.value = categories.value.map((category, i) => (i === index ? updatedCategory : category));
};

const removeCategory = (index: number): void => {
    const category = categories.value[index];

    VxSwal.confirmation('Are you sure?', `Are you sure you want to delete "${category?.name}"?`, 'danger', async () => {
        categories.value = categories.value.filter((_, i) => i !== index);
    });
};

const onCategorySaved = (data: ThreadCategoryFormData, editIndex: number | null): void => {
    if (null !== editIndex && undefined !== categories.value[editIndex]) {
        categories.value = categories.value.map((category, i) =>
            i === editIndex ? { ...category, ...data } : category
        );
    } else {
        categories.value = [...categories.value, { ...data } as ThreadCategoryType];
    }
    modalVisible.value = false;
};

const save = async (): Promise<void> => {
    if (submitting.value || !dirty.value) {
        return;
    }

    submitting.value = true;

    try {
        const response = await threadCategoriesClient.updateThreadCategories(
            { teamId },
            { categories: categories.value.map(toFormData) }
        );
        categories.value = response.data.categories;
        originalCategories.value = response.data.categories.map((category) => ({ ...category }));
        toasted.success('Categories saved');
    } catch (err) {
        toasted.error(catchRequestError(err));
    } finally {
        submitting.value = false;
    }
};

const sortableEl = ref<HTMLElement | null>(null);

const { start: startSortable } = useSortable(sortableEl, categories, {
    handle: '.category-handle',
    draggable: '.category-draggable',
    animation: 200,
    onUpdate: (e: { oldIndex: number; newIndex: number }) => {
        moveArrayElement(categories.value, e.oldIndex, e.newIndex);
    },
} as Record<string, unknown>);

watch(loading, async (isLoading) => {
    if (!isLoading && !loadError.value) {
        await nextTick();
        startSortable();
    }
});

const fetchCategories = async (): Promise<void> => {
    loading.value = true;
    loadError.value = false;

    try {
        const response = await threadCategoriesClient.getThreadCategories({ teamId });
        categories.value = response.data.categories;
        originalCategories.value = response.data.categories.map((category) => ({ ...category }));
    } catch {
        loadError.value = true;
    } finally {
        loading.value = false;
    }
};

fetchCategories();
</script>
